// Controls of the product editor: MediaDropzone, Switch, Snackbar alerts,
// sortable lists, the HTML snippet editor and small icon buttons.
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Reorder, useDragControls } from "framer-motion"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import { twMerge } from "tailwind-merge"
import { Bars2Icon, TrashIcon, XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import MediaField from "../../../AdminShared/MediaField"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

/** MUI Switch analogue with a label on the right */
export const Switch = ({ checked, onChange, label, disabled = false, className = "", ...rest }) => (
  <label className={twMerge("inline-flex items-center gap-2 text-sm text-admin_text select-none", disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer", className)}>
    <span className={twMerge("relative inline-block w-10 h-6 rounded-full transition-colors", checked ? "bg-admin_primary" : "bg-admin_neutral_2")}>
      <input type="checkbox" role="switch" className="sr-only" checked={!!checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} {...rest} />
      <span className={twMerge("absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform", checked ? "translate-x-4" : "")} />
    </span>
    {label && <span>{label}</span>}
  </label>
)

export const Checkbox = ({ checked, onChange, label, disabled = false, className = "", ...rest }) => (
  <label className={twMerge("inline-flex items-center gap-2 text-sm text-admin_text cursor-pointer select-none", className)}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="w-4 h-4 accent-admin_primary" {...rest} />
    {label && <span>{label}</span>}
  </label>
)

export const IconButton = ({ children, className = "", title, ...rest }) => (
  <button type="button" title={title} className={twMerge("inline-flex items-center justify-center w-8 h-8 rounded-full text-admin_text_secondary hover:bg-admin_neutral_1 hover:text-admin_text disabled:opacity-40 disabled:cursor-not-allowed transition-colors", className)} {...rest}>
    {children}
  </button>
)

export const DeleteIconButton = (props) => {
  const t = useProductEditorTexts()
  return (
    <IconButton title={t`Delete`} className="hover:text-admin_error" {...props}>
      <TrashIcon className="w-4 h-4" />
    </IconButton>
  )
}

export const Alert = ({ severity = "info", children, onClose, className = "" }) => {
  const tone = { error: "bg-red-50 border-red-200 text-red-800", warning: "bg-amber-50 border-amber-200 text-amber-800", success: "bg-green-50 border-green-200 text-green-800", info: "bg-blue-50 border-blue-200 text-blue-800" }[severity]
  return (
    <div role="alert" className={twMerge("flex items-start gap-2 border rounded-md px-3 py-2 text-sm", tone, className)}>
      <InformationCircleIcon className="w-5 h-5 shrink-0" />
      <div className="flex-1">{children}</div>
      {onClose && (
        <button type="button" onClick={onClose} className="opacity-70 hover:opacity-100">
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/** Top-centre auto-hiding message (MUI Snackbar) */
export const Snackbar = ({ message, severity = "error", onClose, duration = 6000 }) => {
  useEffect(() => {
    if (!message) return
    const t = setTimeout(onClose, duration)
    return () => clearTimeout(t)
  }, [message])
  if (!message) return null
  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-[min(90%,600px)] shadow-lg" data-testid="editor-snackbar">
      <Alert severity={severity} onClose={onClose}>
        {message}
      </Alert>
    </div>
  )
}

export const Divider = ({ className = "" }) => <hr className={twMerge("border-0 border-t border-admin_bordered my-3", className)} />

export const Chip = ({ children, color = "default", className = "" }) => {
  const tone = { default: "border-admin_bordered text-admin_text", success: "border-green-500 text-green-700", warning: "border-amber-500 text-amber-700", primary: "border-admin_primary text-admin_primary", error: "border-red-500 text-red-700", info: "border-blue-500 text-blue-700" }[color]
  return <span className={twMerge("inline-flex items-center h-7 px-2.5 rounded-full border text-xs font-medium whitespace-nowrap", tone, className)}>{children}</span>
}

/**
 * Drop-or-click image target with the selected image behind — a large MediaField, so a click, a drop or ⌘V opens the
 * framework AddMediaDialog (paste / image url / crop) before the upload.
 * `upload(file, mimeType)` is the call site's endpoint, `onUploaded(result)`
 * its result.
 */
// `aspect` ("1024/1024", 16 / 9 …) keeps the box the shape of the image it
// takes, the height following the width; without it the box is `height` tall
export function MediaDropzone({ selected, upload, onUploaded, onClear, name, height = "250px", aspect = null, uploading, prompt, disabled, className = "", "data-testid": testId = "media-dropzone", ...rest }) {
  return (
    <MediaField
      src={selected}
      name={name}
      upload={upload}
      onUploaded={onUploaded}
      onClear={onClear}
      prompt={prompt}
      busy={uploading}
      disabled={disabled}
      boxClassName={twMerge("w-full rounded-2xl border-[3px] border-gray-400", className)}
      boxStyle={aspect ? { aspectRatio: String(aspect), height: "auto" } : { height }}
      className="w-full"
      data-testid={testId}
      {...rest}
    />
  )
}

/** The nearest box that scrolls (the editor's pane), which a drag near its edge moves. */
const scrollerOf = (el) => {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    const { overflowY } = getComputedStyle(n)
    if (/(auto|scroll)/.test(overflowY) && n.scrollHeight > n.clientHeight) return n
  }
  return null
}

/** One row of a SortableList: carried by its handle, lifted while it is. */
function SortableRow({ entry, index, render, disabled, onDragEnd, className }) {
  const t = useProductEditorTexts()
  const controls = useDragControls()
  const rowRef = useRef(null)
  // near the top or bottom edge of the pane the list scrolls under the row
  const autoScroll = (event, info) => {
    const scroller = scrollerOf(rowRef.current)
    if (!scroller) return
    const box = scroller.getBoundingClientRect()
    const y = info.point.y - window.scrollY
    const edge = 60
    if (y < box.top + edge) scroller.scrollTop -= Math.ceil((box.top + edge - y) / 4)
    else if (y > box.bottom - edge) scroller.scrollTop += Math.ceil((y - (box.bottom - edge)) / 4)
  }
  // touch-action none: a finger on the handle drags the row instead of scrolling the page
  const handle = (
    <span
      onPointerDown={(e) => {
        if (disabled) return
        e.preventDefault()
        controls.start(e)
      }}
      title={t`Drag to reorder`}
      className={twMerge("inline-flex items-center justify-center w-6 h-6 text-admin_text_secondary shrink-0 select-none touch-none", disabled ? "cursor-default" : "cursor-grab active:cursor-grabbing")}
      data-testid="drag-handle"
    >
      <Bars2Icon className="w-5 h-5" />
    </span>
  )
  return (
    <Reorder.Item
      as="div"
      ref={rowRef}
      value={entry}
      dragListener={false}
      dragControls={controls}
      onDrag={autoScroll}
      onDragEnd={() => onDragEnd(entry)}
      whileDrag={{ scale: 1.02, boxShadow: "0 12px 28px rgba(0,0,0,0.18)", zIndex: 30 }}
      className={twMerge("relative rounded-md", className)}
    >
      {render(entry.item, index, handle)}
    </Reorder.Item>
  )
}

/**
 * Vertical drag-and-drop list, as the dashboards' Customize list has it
 * (framer-motion Reorder): a row is taken by its handle — a mouse, a finger
 * or a pen — and lifts, the others slide aside as it passes, and
 * `onMove(from, to)` is called once when it is let go. `render(item, index,
 * handle)` gets the row's place while it is carried, so what marks the first
 * row moves with it.
 */
export function SortableList({ items, keyOf, render, onMove, disabled = false, className = "", itemClassName = "" }) {
  const entries = useMemo(() => items.map((item, index) => ({ key: keyOf(item, index), item, index })), [items])
  // the order while a row is carried
  const [order, setOrder] = useState(entries)
  useEffect(() => setOrder(entries), [entries])
  const orderRef = useRef(order)
  orderRef.current = order
  const landed = (entry) => {
    const to = orderRef.current.indexOf(entry)
    if (to < 0 || to === entry.index) return
    onMove(entry.index, to)
  }
  return (
    <Reorder.Group as="div" axis="y" values={order} onReorder={setOrder} className={twMerge("flex flex-col", className)}>
      {order.map((entry, index) => (
        <SortableRow key={entry.key} entry={entry} index={index} render={render} disabled={disabled} onDragEnd={landed} className={itemClassName} />
      ))}
    </Reorder.Group>
  )
}

/** Full-screen HTML editor with a live preview */
export function HTMLSnippetEditor({ open, value, onChange, onClose, title = "HTML Snippet Editor" }) {
  const t = useProductEditorTexts()
  const [html, setHtml] = useState(value || "")
  useEffect(() => {
    if (open) setHtml(value || "")
  }, [open, value])
  if (!open) return null
  return (
    <Modal isOpen onClose={onClose} variant="admin_fullScreen" title={t(title)} allowCloseBtn={false} styleOverrides={{ container: "bg-admin_paper w-[96vw] h-[94vh] rounded-md border border-admin_bordered shadow-lg flex flex-col overflow-hidden", contentWrapper: "flex-1 min-h-0 flex flex-col", content: "flex-1 min-h-0 flex flex-col p-0" }}>
      {/* code beside the preview; on a phone one above the other */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row border-t border-admin_bordered" data-testid="html-editor">
        <div className="w-full md:w-1/2 flex-1 md:flex-none min-h-0 flex flex-col border-b md:border-b-0 md:border-r border-admin_bordered">
          <div className="px-4 py-2 border-b border-admin_bordered text-sm font-semibold">{t`HTML Code`}</div>
          <textarea value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false} className="flex-1 w-full resize-none p-3 font-mono text-[13px] leading-5 bg-[#1e1e1e] text-gray-100 outline-none" />
        </div>
        <div className="w-full md:w-1/2 flex-1 md:flex-none min-h-0 flex flex-col">
          <div className="px-4 py-2 border-b border-admin_bordered text-sm font-semibold">{t`Preview`}</div>
          <div className="flex-1 overflow-auto p-6 prose max-w-none text-admin_text [&_img]:max-w-full [&_a]:text-admin_primary [&_a]:underline" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
      <div className="flex justify-end gap-3 p-3 border-t border-admin_bordered">
        <Button variant="admin_secondary" onClick={onClose}>{t`Cancel`}</Button>
        <Button variant="admin_primary" onClick={() => onChange(html)}>{t`Save`}</Button>
      </div>
    </Modal>
  )
}

/** Input with a leading adornment text (MUI InputAdornment) */
export const AdornedInput = ({ adornment, value, onChange, type = "text", disabled, className = "", inputClassName = "", ...rest }) => (
  <div className={twMerge("flex items-center h-[38px] rounded-md border border-admin_bordered bg-admin_paper text-sm overflow-hidden focus-within:border-admin_primary", disabled ? "opacity-60" : "", className)}>
    {adornment && <span className="px-3 text-admin_text_secondary whitespace-nowrap border-r border-admin_bordered h-full flex items-center bg-admin_neutral_1/40">{adornment}</span>}
    <input type={type} value={value ?? ""} disabled={disabled} onChange={(e) => onChange?.(e.target.value)} className={twMerge("flex-1 min-w-0 h-full px-3 bg-transparent outline-none text-admin_text", inputClassName)} {...rest} />
  </div>
)

export const HelperText = ({ children, error = false }) => <span className={twMerge("text-[11px]", error ? "text-admin_error" : "text-admin_text_secondary")}>{children}</span>

